import React from 'react';
import type { ReactNode } from 'react';
import { FormFieldShell } from '@/shared/widgets/form';
import { Input } from '@/shared/widgets/input';
import { cn } from '@/shared/utils/cn';
import {
  validateCardPaymentFormValues,
  type CardPaymentField,
} from '../utils/cardPaymentFormValidation';

interface CardPaymentFormProps {
  formId: string;
  cardName: string;
  cardNumber: string;
  cardExpiry: string;
  cardCvv: string;
  saveCard: boolean;
  onCardNameChange: (value: string) => void;
  onCardNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onExpiryChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCvvChange: (value: string) => void;
  onSaveCardChange: (checked: boolean) => void;
  cardNetwork?: string;
  cardNumberError?: string;
  cvvLength?: number;
  isExpiryInvalid?: boolean;
  variant?: 'desktop' | 'mobile';
}

const CARD_NETWORK_LABELS: Record<string, string> = {
  visa: 'VISA',
  mastercard: 'MasterCard',
  amex: 'AMEX',
  rupay: 'RuPay',
  maestro: 'Maestro',
};

type TouchedCardFields = Partial<Record<CardPaymentField, boolean>>;

interface CardInputFieldProps {
  label: string;
  error?: string;
  children: ReactNode;
  className?: string;
}

const CardInputField: React.FC<CardInputFieldProps> = ({
  label,
  error,
  children,
  className,
}) => (
  <FormFieldShell
    variant="stacked"
    label={label}
    error={error}
    required
    className={className}
  >
    {children}
  </FormFieldShell>
);

export const CardPaymentForm: React.FC<CardPaymentFormProps> = ({
  formId,
  cardName,
  cardNumber,
  cardExpiry,
  cardCvv,
  saveCard,
  onCardNameChange,
  onCardNumberChange,
  onExpiryChange,
  onCvvChange,
  onSaveCardChange,
  cardNetwork,
  cardNumberError,
  cvvLength = 3,
  isExpiryInvalid = false,
  variant = 'mobile',
}) => {
  const [touchedFields, setTouchedFields] = React.useState<TouchedCardFields>({});
  const cardNumberInputId = `${formId}-card-number`;
  const cardNetworkLabel = cardNetwork
    ? CARD_NETWORK_LABELS[cardNetwork.trim().toLowerCase()] || cardNetwork
    : '';
  const outlinedInputClass =
    'h-12 w-full rounded-[5px] border border-[#93A2B3] bg-white px-4 text-sm font-medium tracking-[0.04em] text-[#011E38] outline-none transition-colors placeholder:text-[#9B9B9B] focus:border-[#6F7F91]';
  const validation = React.useMemo(
    () =>
      validateCardPaymentFormValues({
        cardName,
        cardNumber,
        cardExpiry,
        cardCvv,
        cvvLength,
      }),
    [cardCvv, cardExpiry, cardName, cardNumber, cvvLength]
  );
  const isDesktop = variant === 'desktop';
  const gridGapClass = isDesktop ? 'gap-6' : 'gap-4';
  const cardNetworkPositionClass = isDesktop ? 'right-4' : 'right-3';

  const markFieldTouched = (field: CardPaymentField) => {
    setTouchedFields((current) => ({ ...current, [field]: true }));
  };

  const getFieldError = (field: CardPaymentField): string | undefined => {
    if (field === 'cardNumber' && cardNumberError) return cardNumberError;
    if (field === 'cardExpiry' && isExpiryInvalid) {
      return validation.errors.cardExpiry || 'Enter a valid expiry date';
    }
    return touchedFields[field] ? validation.errors[field] : undefined;
  };

  const cardNameError = getFieldError('cardName');
  const cardNumberValidationError = getFieldError('cardNumber');
  const cardExpiryError = getFieldError('cardExpiry');
  const cardCvvError = getFieldError('cardCvv');
  const getInputClass = (hasError: boolean, className?: string) =>
    cn(outlinedInputClass, hasError && 'border-red-500 focus:border-red-500', className);

  return (
    <form
      id={formId}
      data-payvialink-card-form="true"
      className={cn('space-y-4', !isDesktop && '[&_.mb-1]:hidden [&_.mt-1.h-5:empty]:hidden')}
      onSubmit={(e) => e.preventDefault()}
    >
      {/*!isDesktop && <h4 className="text-sm font-bold text-[#011E38]">Enter Card Details</h4>*/}
      <div className="space-y-4">
        <CardInputField label="Name on the card" error={cardNameError}>
          <Input
            type="text"
            value={cardName}
            onChange={(e) => onCardNameChange(e.target.value.replace(/[^A-Za-z ]+/g, ''))}
            onBlur={() => markFieldTouched('cardName')}
            className={cn(getInputClass(Boolean(cardNameError)), 'uppercase')}
            placeholder="NAME ON THE CARD"
            autoComplete="cc-name"
            aria-invalid={Boolean(cardNameError)}
          />
        </CardInputField>

        <CardInputField label="Card number" error={cardNumberValidationError}>
          <div className="relative">
            <Input
              id={cardNumberInputId}
              data-payvialink-card-number="true"
              type="text"
              value={cardNumber}
              onChange={onCardNumberChange}
              onBlur={() => markFieldTouched('cardNumber')}
              className={getInputClass(Boolean(cardNumberValidationError), 'pr-24')}
              placeholder="CARD NUMBER"
              inputMode="numeric"
              autoComplete="cc-number"
              aria-invalid={Boolean(cardNumberValidationError)}
            />
            {cardNetworkLabel && (
              <span
                className={cn(
                  'pointer-events-none absolute top-1/2 -translate-y-1/2 text-sm font-black text-black',
                  cardNetworkPositionClass
                )}
              >
                {cardNetworkLabel}
              </span>
            )}
          </div>
        </CardInputField>

        <div className={cn('grid grid-cols-2', gridGapClass)}>
          <CardInputField label="Expiry" error={cardExpiryError}>
            <Input
              id={`${formId}-expiry-date`}
              data-payvialink-expiry="true"
              type="text"
              value={cardExpiry}
              onChange={onExpiryChange}
              onBlur={() => markFieldTouched('cardExpiry')}
              className={getInputClass(Boolean(cardExpiryError))}
              placeholder="MM/YY"
              maxLength={7}
              inputMode="numeric"
              autoComplete="cc-exp"
              aria-invalid={Boolean(cardExpiryError)}
            />
          </CardInputField>

          <CardInputField label="CVV" error={cardCvvError}>
            <Input
              id={`${formId}-cvv-card`}
              data-payvialink-cvv="true"
              type="password"
              value={cardCvv}
              onChange={(e) => onCvvChange(e.target.value.replace(/\D/g, '').substring(0, cvvLength))}
              onBlur={() => markFieldTouched('cardCvv')}
              className={getInputClass(Boolean(cardCvvError))}
              placeholder="CVV"
              maxLength={cvvLength}
              inputMode="numeric"
              autoComplete="cc-csc"
              aria-invalid={Boolean(cardCvvError)}
            />
          </CardInputField>
        </div>

        <div className="flex items-center space-x-3 pt-1">
          <Input
            type="checkbox"
            id={`${formId}-save-card`}
            checked={saveCard}
            onChange={(e) => onSaveCardChange(e.target.checked)}
            className="h-5 w-5 rounded-[3px] border-2 border-[#8B8B8B] p-0 text-[#011E38] focus:ring-[#011E38]"
          />
          <label
            htmlFor={`${formId}-save-card`}
            className={cn('cursor-pointer select-none font-medium tracking-[0.01em]',isDesktop ? 'text-sm text-[#999999]' : 'whitespace-nowrap text-[11px] font-semibold text-[#EF5D35]')}
          >
            Save your card details for future purchases
          </label>
        </div>
      </div>
    </form>
  );
};

export default CardPaymentForm;
